import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMinus, FiPlus, FiUsers } from "react-icons/fi";
import { ApiEvent } from "./ApiEvent";
import { GetLanguageString } from "../helper/Components";
import "./ahbabManualCount.css";

const AUTO_SAVE_DELAY_MS = 800;

// Total ahbab who attended a Halqa Qurani ijtima, including the ones marked in the list below.
// Saves by itself (Enter, leaving the field, or a moment after the +/- buttons). Renders nothing for any other event.
const AhbabManualCount = ({ eventId, refreshKey }) => {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorKey, setErrorKey] = useState("");
  const timer = useRef(null);

  const applyInfo = useCallback((data) => {
    setInfo(data);
    setDraft(data.manualCount === null ? "" : String(data.manualCount));
  }, []);

  useEffect(() => {
    ApiEvent()
      .getAhbabManualCount(eventId)
      .then((res) => applyInfo(res.data))
      .catch((err) => console.log(err));
  }, [eventId, refreshKey, applyInfo]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const save = (count) => {
    clearTimeout(timer.current);
    setStatus("saving");
    setErrorKey("");
    ApiEvent()
      .saveAhbabManualCount(eventId, count)
      .then((res) => {
        applyInfo(res.data);
        setStatus("saved");
        timer.current = setTimeout(() => setStatus("idle"), 2500);
      })
      .catch((err) => {
        const key = err?.response?.data;
        setErrorKey(typeof key === "string" && key.startsWith("event_") ? key : "please_try_again");
        setStatus("error");
      });
  };

  if (!info || !info.isHalqaQurani) {
    return null;
  }

  const saved = info.manualCount;
  const marked = info.markedCount;
  const draftNumber = draft === "" ? null : Number(draft);
  const isValidNumber = draftNumber === null || (Number.isInteger(draftNumber) && draftNumber >= 0);
  const belowMarked = draftNumber !== null && isValidNumber && draftNumber < marked;
  const invalid = !isValidNumber || belowMarked;
  const remaining = saved === null ? null : Math.max(saved - marked, 0);
  const markedShare = saved ? Math.min(Math.round((marked / saved) * 100), 100) : 0;

  const commit = (value) => {
    if (value === null || !Number.isInteger(value) || value < 0 || value < marked || value === saved) {
      return;
    }
    save(value);
  };

  const change = (text) => {
    setDraft(text);
    setStatus("idle");
    clearTimeout(timer.current);
  };

  const step = (delta) => {
    const next = Math.max((draftNumber === null ? marked : draftNumber) + delta, 0);
    setDraft(String(next));
    setStatus("idle");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => commit(next), AUTO_SAVE_DELAY_MS);
  };

  return (
    <div className="amc-card">
      <div className="amc-head">
        <span className="amc-head-icon">
          <FiUsers />
        </span>
        <div className="amc-head-text">
          <div className="amc-title">
            <GetLanguageString props="events_ahbab_manual_count_title" />
          </div>
          <div className="amc-hint">
            <GetLanguageString props="events_ahbab_manual_count_hint" />
          </div>
        </div>
      </div>

      <div className="amc-body">
        <div className="amc-tiles">
          <div className="amc-tile amc-tile-total">
            <b>{saved === null ? "—" : saved}</b>
            <span>
              <GetLanguageString props="events_ahbab_manual_count_total" />
            </span>
          </div>
          <div className="amc-tile amc-tile-marked">
            <b>{marked}</b>
            <span>
              <GetLanguageString props="events_ahbab_manual_count_marked" />
            </span>
          </div>
          <div className="amc-tile amc-tile-rest">
            <b>{remaining === null ? "—" : remaining}</b>
            <span>
              <GetLanguageString props="events_ahbab_manual_count_unmarked" />
            </span>
          </div>
        </div>

        {saved !== null && (
          <div className="amc-bar" aria-hidden="true">
            <div className="amc-bar-fill" style={{ width: `${markedShare}%` }} />
          </div>
        )}

        {info.canEdit && (
          <div className="amc-edit">
            <div className="amc-stepper">
              <button type="button" className="amc-step" onClick={() => step(-1)} aria-label="-1">
                <FiMinus />
              </button>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                className={`amc-input${invalid ? " amc-input-error" : ""}`}
                value={draft}
                placeholder="0"
                onChange={(e) => change(e.target.value)}
                onBlur={() => commit(draftNumber)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commit(draftNumber);
                  }
                }}
              />
              <button type="button" className="amc-step" onClick={() => step(1)} aria-label="+1">
                <FiPlus />
              </button>
            </div>

            <span className="amc-status" aria-live="polite">
              {status === "saving" && <span className="amc-pill amc-pill-saving"><GetLanguageString props="events_ahbab_manual_count_saving" /></span>}
              {status === "saved" && <span className="amc-pill amc-pill-saved"><GetLanguageString props="events_ahbab_manual_count_saved" /></span>}
              {status === "error" && <span className="amc-pill amc-pill-error">{t(errorKey)}</span>}
            </span>

            {saved !== null && (
              <button type="button" className="amc-clear" disabled={status === "saving"} onClick={() => save(null)}>
                <GetLanguageString props="events_ahbab_manual_count_clear" />
              </button>
            )}
          </div>
        )}

        {info.canEdit && belowMarked && (
          <div className="amc-error">
            <GetLanguageString props="events_ahbab_manual_count_min" value={marked} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AhbabManualCount;
